import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpen, Bug, ChevronRight, CircleHelp, Code2, FileText, MessagesSquare, Ticket, type LucideIcon } from "lucide-react";
import {
  ASK_RUN_PHASE_LABELS,
  askHandoffNavigationState,
  askVerdictLabel,
  buildAskHandoff,
  isAskHandoffProminent,
  type AskCodeRef,
  type AskScope,
  type AskSourceKind,
  type AskSpecAgreement,
  type AskVerdict,
} from "@ttc-spectree/toolbox-contract";
import { MarkdownRenderer } from "@/components/common/markdown-renderer";
import { Chip } from "@/components/grooming/grooming-chips";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useMe } from "@/hooks/queries/use-me";
import { canAnywhere, useToolboxAccess } from "@/hooks/queries/use-toolbox-access";
import type { AskAnswerMessage, AskChatMessage } from "@/hooks/use-ask-conversation";
import {
  ASK_CODE_ONLY_WARNING,
  ASK_HELP_REDIRECT_LINE,
  ASK_SPEC_DISAGREES_LINE,
  askDisplayText,
  askVerdictTone,
} from "@/lib/toolbox/ask";
import { FOGBUGZ_FORMULATOR_PATH } from "@/lib/toolbox/tools";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";
import { cn } from "@/lib/utils";
import { useHelpPanelStore } from "@/stores/help-panel-store";

/**
 * One Product Q&A answer (epic PI-SPEC-SW-E310, feature PI-SPEC-SW-1628,
 * ER-1667 Package F, Resolved Decision 10).
 *
 * - While it streams: the activity phrase (`ASK_RUN_PHASE_LABELS`) and the
 *   text so far with a caret; the `SPECTREE_ASK_TURN` marker never shows.
 * - Once answered: exactly one verdict chip (`askVerdictLabel`), the plain
 *   answer (markdown, no links, no images), a warning for `code-only`, a
 *   "may be a bug" line when `specAgrees` is `no`, the written-spec sources
 *   (repo docs, ADO work items, FogBugz cases; code sources are paths, so
 *   they stay in the fold), and the
 *   "Where in the code" fold. File paths appear only inside the fold, which
 *   starts open for Admins and Developers and closed for everyone else
 *   (`codeFoldOpenByDefault`); the person can toggle it.
 * - `off-topic` with `redirect: "help"`: one line and an Open Help button.
 *
 * - "Start FogBugz Formulator" (feature PI-SPEC-SW-1631, Resolved Decision
 *   10c): on an answered, on-topic answer, for someone with the `fogbugz`
 *   capability on some project and a saved FogBugz key (the FogBugz
 *   Formulator tile's own gate). It opens the Toolbox FogBugz Formulator with
 *   the question, the plain answer and the code references as navigation
 *   state (`askHandoffNavigationState`), never in the URL and never sent to
 *   SpecTree. It is emphasised when the verdict is `code-only` or the spec
 *   disagrees (`isAskHandoffProminent`). The person still chooses the routing
 *   and confirms every slot there.
 */

/** The hand-off button's words. */
export const START_FOGBUGZ_FORMULATOR_LABEL = "Start FogBugz Formulator";

/**
 * The question an answer replies to: the nearest person message before
 * `index`. Pure; exported for tests and the page.
 */
export function askQuestionBefore(messages: readonly AskChatMessage[], index: number): string | undefined {
  for (let i = Math.min(index, messages.length) - 1; i >= 0; i -= 1) {
    const message = messages[i];
    if (message?.role === "person") return message.text;
  }
  return undefined;
}

const SOURCE_ICONS: Readonly<Record<AskSourceKind, LucideIcon>> = {
  code: Code2,
  "repo-doc": FileText,
  "ado-work-item": BookOpen,
  "fogbugz-case": Ticket,
};

const smallButton =
  "inline-flex h-[26px] items-center gap-1.5 whitespace-nowrap rounded-lg border border-[#27272A] bg-[#1A1A1E] px-[9px] text-xs font-medium text-[#FAFAFA] transition-colors hover:bg-[#222226]";

/** "repo · path:12-30" for a code reference. Pure; exported for tests. */
export function askCodeRefLocation(ref: AskCodeRef): string {
  const lines =
    ref.startLine === undefined
      ? ""
      : ref.endLine !== undefined && ref.endLine !== ref.startLine
        ? `:${String(ref.startLine)}-${String(ref.endLine)}`
        : `:${String(ref.startLine)}`;
  return `${ref.repo} · ${ref.path}${lines}`;
}

/** Answer markdown may never link or load anything. */
const noLinks = () => false;

function AskWho() {
  return (
    <span className="flex items-center gap-1.5 text-[11px] font-semibold tracking-[0.02em] text-muted-foreground">
      <span className="flex h-4 w-4 items-center justify-center rounded-[5px] border border-[#27272A] bg-[#1A1A1E]">
        <MessagesSquare className="h-[11px] w-[11px] text-[#D4D4D8]" aria-hidden="true" />
      </span>
      Product Q&amp;A
    </span>
  );
}

function CodeFold({ codeRefs, defaultOpen }: { codeRefs: readonly AskCodeRef[]; defaultOpen: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Collapsible open={open} onOpenChange={setOpen} data-testid="ask-code-fold" data-open={open ? "true" : "false"}>
      <CollapsibleTrigger
        className="flex items-center gap-1.5 text-xs font-medium text-[#6CC4FF] hover:text-[#23A4FF]"
        data-testid="ask-code-fold-toggle"
      >
        <ChevronRight className={cn("h-3.5 w-3.5 transition-transform", open && "rotate-90")} aria-hidden="true" />
        Where in the code
        <span className={TOOLBOX_TEXT.faint}>({codeRefs.length})</span>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ul className="mt-2 flex flex-col gap-1.5" data-testid="ask-code-refs">
          {codeRefs.map((ref, index) => (
            <li
              key={`${ref.repo}-${ref.path}-${String(ref.startLine ?? 0)}-${String(index)}`}
              className="flex flex-col gap-0.5 rounded-md border border-[#1F1F23] bg-[#121215] px-2.5 py-1.5"
            >
              <span className="break-all font-mono text-[11px] text-[#D4D4D8]">{askCodeRefLocation(ref)}</span>
              <span className="text-xs text-muted-foreground">{ref.note}</span>
            </li>
          ))}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  );
}

function StartFogbugzFormulator({
  question,
  answer,
  verdict,
  specAgrees,
  codeRefs,
  scope,
}: {
  question: string;
  answer: string;
  verdict: AskVerdict;
  specAgrees?: AskSpecAgreement | undefined;
  codeRefs: readonly AskCodeRef[];
  scope: AskScope;
}) {
  const navigate = useNavigate();
  const { data: me } = useMe();
  const { data: access } = useToolboxAccess();
  // The FogBugz Formulator tile's gate: the `fogbugz` capability somewhere and a saved key.
  if (!canAnywhere(access, "fogbugz") || me?.hasFogbugzKey !== true) return null;
  const handoff = buildAskHandoff({ question, answer, verdict, specAgrees, codeRefs, scope });
  if (!handoff) return null;
  const prominent = isAskHandoffProminent(verdict, specAgrees);
  const start = () => {
    void navigate(FOGBUGZ_FORMULATOR_PATH, { state: askHandoffNavigationState(handoff) });
  };
  if (prominent) {
    return (
      <div
        className="flex flex-wrap items-center gap-2 rounded-lg border border-[#3F1D1D] bg-[#1C1012] px-3 py-2"
        data-testid="ask-start-fogbugz-formulator-card"
      >
        <span className="min-w-0 flex-1 text-xs text-[#FCA5A5]">
          Looks like a bug? File it in FogBugz with this answer as a starting point.
        </span>
        <Button size="sm" onClick={start} data-testid="ask-start-fogbugz-formulator" data-prominent="true">
          <Bug aria-hidden="true" />
          {START_FOGBUGZ_FORMULATOR_LABEL}
        </Button>
      </div>
    );
  }
  return (
    <div>
      <button
        type="button"
        className={smallButton}
        onClick={start}
        data-testid="ask-start-fogbugz-formulator"
        data-prominent="false"
      >
        <Bug className="h-[13px] w-[13px]" aria-hidden="true" />
        {START_FOGBUGZ_FORMULATOR_LABEL}
      </button>
    </div>
  );
}

export interface AskAnswerProps {
  message: AskAnswerMessage;
  /** Whether "Where in the code" starts open: the product's `codeFoldOpen` for the person's roles. */
  codeFoldOpenByDefault: boolean;
  /**
   * The question this answer replies to (`askQuestionBefore`) and the
   * product it was about. With both, an answered, on-topic answer offers
   * "Start FogBugz Formulator" to someone allowed to file FogBugz cases.
   */
  question?: string | undefined;
  scope?: AskScope | undefined;
}

export function AskAnswer({ message, codeFoldOpenByDefault, question, scope }: AskAnswerProps) {
  const openHelp = useHelpPanelStore((state) => state.setOpen);
  const text = askDisplayText(message.text, message.streaming);
  const waiting = message.streaming && text.length === 0;
  const answered = !message.streaming && message.outcome === "answered";
  const verdict = answered ? (message.verdict ?? "not-sure") : null;
  const offTopic = verdict === "off-topic";
  const toHelp = offTopic && message.redirect === "help";
  // Code sources are file paths: they live in the fold only, never beside the plain answer.
  const specSources = message.sources.filter((source) => source.kind !== "code");

  return (
    <div
      className="flex flex-col gap-2 text-[13px] leading-[1.55] text-[#E4E4E7]"
      data-testid="ask-answer"
      data-streaming={message.streaming ? "true" : "false"}
      data-verdict={verdict ?? ""}
      aria-busy={message.streaming}
    >
      <div className="flex flex-wrap items-center gap-2">
        <AskWho />
        {verdict && (
          <Chip tone={askVerdictTone(verdict, message.specAgrees)} testId="ask-verdict">
            {askVerdictLabel(verdict, message.specAgrees)}
          </Chip>
        )}
      </div>
      {message.streaming && (
        <span className="flex items-center gap-1.5 text-xs text-[#6CC4FF]" data-testid="ask-activity">
          <span
            className="h-2.5 w-2.5 flex-none animate-spin rounded-full border-2 border-[#23A4FF] border-r-transparent"
            aria-hidden="true"
          />
          {ASK_RUN_PHASE_LABELS[message.phase ?? "starting"]}…
        </span>
      )}
      {toHelp ? (
        <div className="flex flex-wrap items-center gap-2" data-testid="ask-help-redirect">
          <span>{ASK_HELP_REDIRECT_LINE}</span>
          <button
            type="button"
            className={smallButton}
            onClick={() => {
              openHelp(true);
            }}
            data-testid="ask-open-help"
          >
            <CircleHelp className="h-[13px] w-[13px]" aria-hidden="true" />
            Open Help
          </button>
        </div>
      ) : (
        !waiting &&
        text.length > 0 && (
          <div className={cn(message.streaming && "[&>div>*:last-child]:inline [&>div]:inline")} data-testid="ask-answer-text">
            <MarkdownRenderer
              content={text}
              allowLink={noLinks}
              className="[&_ol]:list-outside [&_ol]:pl-[18px] [&_p]:mb-2 [&_ul]:list-outside [&_ul]:pl-[18px]"
            />
            {message.streaming && (
              <span
                className="ml-0.5 inline-block h-[14px] w-[7px] rounded-[1px] bg-[#6CC4FF] align-[-2px]"
                data-testid="ask-caret"
                aria-hidden="true"
              />
            )}
          </div>
        )
      )}
      {waiting && (
        <span
          className="inline-block h-[14px] w-[7px] rounded-[1px] bg-[#6CC4FF]"
          data-testid="ask-caret"
          aria-hidden="true"
        />
      )}
      {message.outcome === "stopped" && <span className={cn("text-xs", TOOLBOX_TEXT.faint)}>Stopped.</span>}
      {message.outcome === "failed" && (
        <span role="alert" className="text-[13px] text-[#FBBF24]" data-testid="ask-answer-error">
          {message.errorMessage ?? "Something went wrong. Ask again."}
        </span>
      )}
      {verdict === "code-only" && (
        <span className="text-xs text-[#FDE68A]" data-testid="ask-code-only-warning">
          {ASK_CODE_ONLY_WARNING}
        </span>
      )}
      {verdict === "spec-found" && message.specAgrees === "no" && (
        <span role="status" className="text-[13px] font-medium text-[#FCA5A5]" data-testid="ask-spec-disagrees">
          {ASK_SPEC_DISAGREES_LINE}
        </span>
      )}
      {answered && !offTopic && specSources.length > 0 && (
        <div className="flex flex-wrap gap-1.5" aria-label="Sources" data-testid="ask-sources">
          {specSources.map((source, index) => {
            const Icon = SOURCE_ICONS[source.kind];
            return (
              <span
                key={`${source.kind}-${source.ref}-${String(index)}`}
                className="inline-flex h-[22px] max-w-full items-center gap-[5px] rounded-md border border-[#1F1F23] bg-[#151518] px-2 text-[11px] text-muted-foreground"
                title={source.ref}
              >
                <Icon className="h-3 w-3 flex-none" aria-hidden="true" />
                <span className="truncate">{source.title}</span>
              </span>
            );
          })}
        </div>
      )}
      {answered && !offTopic && message.codeRefs.length > 0 && (
        <CodeFold codeRefs={message.codeRefs} defaultOpen={codeFoldOpenByDefault} />
      )}
      {verdict !== null && !offTopic && question !== undefined && scope !== undefined && (
        <StartFogbugzFormulator
          question={question}
          answer={text}
          verdict={verdict}
          specAgrees={message.specAgrees}
          codeRefs={message.codeRefs}
          scope={scope}
        />
      )}
    </div>
  );
}
